import { useEffect, useState, useRef } from "react";
import api, { formatErr } from "@/lib/api";
import { Save, Monitor, Smartphone, Loader2, CheckCircle2, Upload, Download } from "lucide-react";

export default function Template() {
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [viewport, setViewport] = useState("desktop");
  const fileInput = useRef(null);

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      const t = data[0]; if (!t) return;
      setTpl(t); setSubject(t.subject); setHtml(t.html);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/templates/${tpl.id}`, { subject, html });
      setTpl(data); setDirty(false); setSavedAt(new Date());
    } catch (e) {
      alert(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    if (!/\.(html|htm)$/i.test(file.name)) {
      alert("Please choose a .html file"); return;
    }
    const text = await file.text();
    setHtml(text);
    setDirty(true);
  };

  const downloadCurrent = () => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rahi_feed_current.html"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!tpl) return <div className="p-12 text-sm" style={{ color: "var(--rahi-soft)" }}>Loading template…</div>;

  return (
    <div className="min-h-screen flex flex-col" data-testid="template-page">
      <div className="sticky top-0 z-10 border-b" style={{ borderColor:"var(--rahi-border)", background:"rgba(253,252,251,.95)", backdropFilter:"blur(8px)" }}>
        <div className="px-8 py-5 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color:"var(--rahi-red)" }}>Default template</p>
            <h1 className="font-display text-2xl font-black tracking-tight">{tpl.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            {savedAt && !dirty && (
              <span className="text-xs flex items-center gap-1.5" style={{ color:"#047857" }}>
                <CheckCircle2 size={12}/> Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
            <input ref={fileInput} type="file" accept=".html,.htm" className="hidden"
                   onChange={(e)=>onUploadFile(e.target.files?.[0])} data-testid="template-upload-input"/>
            <button onClick={()=>fileInput.current?.click()} className="btn btn-ghost" data-testid="template-upload-btn" title="Replace HTML from a local file">
              <Upload size={14}/> Upload .html
            </button>
            <button onClick={downloadCurrent} className="btn btn-ghost" data-testid="template-download-btn" title="Download current HTML">
              <Download size={14}/> Download
            </button>
            <button onClick={save} disabled={!dirty || saving} className="btn btn-primary" data-testid="template-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              {saving ? "Saving…" : dirty ? "Save template" : "Saved"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-[420px_1fr]">
        <div className="border-r p-8 space-y-5 overflow-y-auto" style={{ borderColor:"var(--rahi-border)", maxHeight:"calc(100vh - 89px)" }}>
          <p className="text-xs leading-relaxed" style={{ color:"var(--rahi-muted)" }}>
            This is your <strong>master template</strong>. Each new campaign starts from this. Use{" "}
            <span className="font-mono">%recipient.name%</span> for personalization and{" "}
            <span className="font-mono">%recipient.unsubscribe_url%</span> for the unsubscribe link.
          </p>
          <div>
            <label className="label">Default subject line</label>
            <input value={subject} onChange={(e)=>{setSubject(e.target.value); setDirty(true);}} className="input" data-testid="template-subject-input"/>
          </div>
          <div>
            <label className="label">HTML body</label>
            <textarea value={html} onChange={(e)=>{setHtml(e.target.value); setDirty(true);}} className="textarea" rows={26} data-testid="template-html-input"/>
          </div>
        </div>
        <div className="p-8 overflow-y-auto" style={{ background:"#f5f3f0", maxHeight:"calc(100vh - 89px)" }}>
          <div className="flex justify-between items-center mb-4">
            <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color:"var(--rahi-soft)" }}>Preview</p>
            <div className="flex gap-1 p-1 rounded-md border" style={{ borderColor:"var(--rahi-border)", background:"#fff" }}>
              <button onClick={()=>setViewport("desktop")} className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${viewport==="desktop" ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}><Monitor size={12}/> Desktop</button>
              <button onClick={()=>setViewport("mobile")} className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 ${viewport==="mobile" ? "bg-[#1C1917] text-white" : "text-[#57534E]"}`}><Smartphone size={12}/> Mobile</button>
            </div>
          </div>
          <div className="mx-auto bg-white shadow-xl rounded-md overflow-hidden" style={{ width: viewport==="mobile" ? 380 : 720, maxWidth:"100%" }}>
            <iframe
              title="template-preview"
              srcDoc={(html || "")
                .replaceAll("%recipient.name%", "Friend")
                .replaceAll("%recipient.unsubscribe_url%", "#preview-unsubscribe")}
              className="w-full" style={{ minHeight:"80vh", border:0 }}
              data-testid="template-preview-iframe"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
